import { useQuery } from '@tanstack/react-query';
import type { User } from '@task-tracker/shared';
import { apiClient } from '../lib/api-client';
import { CACHE_POLICY } from '../lib/cache-policy';

function normalizeUsers(value: unknown): User[] {
  if (Array.isArray(value)) return value as User[];

  if (value && typeof value === 'object') {
    const maybeData = 'data' in value ? (value as { data?: unknown }).data : undefined;
    if (maybeData && typeof maybeData === 'object' && 'users' in maybeData) {
      const users = (maybeData as { users?: unknown }).users;
      return Array.isArray(users) ? (users as User[]) : [];
    }

    if ('users' in value) {
      const users = (value as { users?: unknown }).users;
      return Array.isArray(users) ? (users as User[]) : [];
    }
  }

  return [];
}

/** Fetches the project user directory used for name resolution across project screens. */
export function useProjectUsers(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-users', projectId],
    queryFn: async () => {
      const result = await apiClient<{ users: User[] }>(`/api/v1/projects/${projectId}/users`);
      return result.data?.users ?? [];
    },
    select: normalizeUsers,
    enabled: !!projectId,
    ...CACHE_POLICY.reference,
  });
}

/** Fetches active users who are not yet project members. */
export function useProjectNonMembers(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['project-non-members', projectId],
    queryFn: async () => {
      const result = await apiClient<{ users: User[] }>(
        `/api/v1/projects/${projectId}/non-members`,
      );
      return result.data?.users ?? [];
    },
    select: normalizeUsers,
    enabled: !!projectId && enabled,
    ...CACHE_POLICY.reference,
  });
}

/** Fetches active users who belong to every selected project. */
export function useAssignableUsers(projectIds: string[]) {
  const normalizedProjectIds = [...new Set(projectIds)].sort();

  return useQuery({
    queryKey: ['assignable-users', normalizedProjectIds],
    queryFn: async () => {
      const query = normalizedProjectIds.join(',');
      const result = await apiClient<{ users: User[] }>(
        `/api/v1/projects/assignable-users?project_ids=${encodeURIComponent(query)}`,
      );
      return result.data?.users ?? [];
    },
    select: normalizeUsers,
    enabled: normalizedProjectIds.length > 0,
    ...CACHE_POLICY.reference,
  });
}
